import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, MapPin, Plus, X } from "lucide-react";
import { API, formatApiError } from "../auth/api";
import WeeklyHours from "../components/WeeklyHours";

/**
 * 2026-10-27: Kunder med pladser uden login (fx ARGO med Pickup) oprettes af ContainerFlow - i kundens
 * sted, til kunden selv kommer på: kunden, pladserne (navn og adresse) og vognmændene pr. plads, som en
 * kunde i ContainerFlow selv vælger dem. Så findes hver kunde og plads kun én gang, også når flere
 * logistikfirmaer kører for den. Logistikfirmaerne sender anmodninger og vedligeholder deres egne
 * materialer, containere, kalendere og navne.
 */
const input = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm";
const btnPrimary = "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50";
const lbl = "block text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1";

function Modal({ title, onClose, children, testid }) {
  return createPortal(
    <div className="fixed inset-0 z-[999] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-5 space-y-4" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center gap-2"><div className="font-bold text-lg flex-1">{title}</div>
          <button onClick={onClose} aria-label="Luk" className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"><X className="w-5 h-5" /></button></div>
        {children}
      </div>
    </div>, document.body);
}

function HaulerPick({ haulers, value, onChange, testid }) {
  if (haulers === null) return <Loader2 className="w-4 h-4 animate-spin text-slate-400" />;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {haulers.map((h) => (
        <label key={h.id} className={`inline-flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-lg border ${value.includes(h.id) ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30" : "border-slate-200 dark:border-slate-600"}`}>
          <input type="checkbox" className="w-4 h-4 accent-emerald-600" checked={value.includes(h.id)}
            onChange={(e) => onChange(e.target.checked ? [...value, h.id] : value.filter((x) => x !== h.id))} />{h.name}
        </label>
      ))}
      {haulers.length === 0 && <span className="text-xs text-slate-500">Ingen logistikfirmaer med Premium endnu.</span>}
    </div>
  );
}

function usePremiumHaulers() {
  const [list, setList] = useState(null);   // null = henter (ellers stod der kort "ingen")
  useEffect(() => { axios.get(`${API}/superadmin/premium-haulers`).then((r) => setList(r.data || [])).catch(() => setList([])); }, []);
  return list;
}

/** Ny kunde med pladser: navn, vognmænd og (valgfrit) de første pladser. */
export function ExtCustomerDialog({ request, onClose, onDone }) {
  const haulers = usePremiumHaulers();
  const [name, setName] = useState(request?.customer_name || "");
  const [chosen, setChosen] = useState(request?.company_id ? [request.company_id] : []);
  const [sites, setSites] = useState([{ name: "", address: "", city: "" }]);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data: c } = await axios.post(`${API}/superadmin/external-customers`, { name, haulers: chosen, request_id: request?.id || "" });
      let made = 0;
      for (const s of sites.filter((x) => x.name.trim())) {
        try { await axios.post(`${API}/superadmin/external-customers/${c.id}/sites`, { ...s, haulers: chosen }); made += 1; }
        catch (e) { toast.error(`${s.name}: ${formatApiError(e)}`); }
      }
      toast.success(`${c.name} er oprettet${made ? ` med ${made} ${made === 1 ? "plads" : "pladser"}` : ""}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <Modal title="Ny kunde med pladser (uden login)" onClose={onClose} testid="sa-ext-new">
      <p className="text-xs text-slate-500">Til en kunde, der ikke selv bruger ContainerFlow (fx ARGO med Pickup). ContainerFlow opretter kunden og pladserne i kundens sted; de valgte logistikfirmaer kører for den og vedligeholder hver deres materialer, containere, leveringsadresser og navne. Kommer kunden selv på, overdrages det hele (Rediger › "Bruger kunden selv ContainerFlow?").</p>
      {request?.note && <p className="text-xs rounded-lg bg-amber-50 dark:bg-amber-900/30 p-2"><b>Fra {request.logistics_name}:</b> {request.note}</p>}
      <div><span className={lbl}>Kundens navn</span><input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Fx ARGO" autoFocus data-testid="sa-ext-name" /></div>
      <div><span className={lbl}>Logistikfirmaer, der kører for kunden</span><HaulerPick haulers={haulers} value={chosen} onChange={setChosen} testid="sa-ext-haulers" /></div>
      <div className="space-y-2">
        <span className={lbl}>Pladser (kan også tilføjes bagefter)</span>
        {sites.map((s, i) => (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input className={input} value={s.name} onChange={(e) => setSites(sites.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="Navn, fx Køge Genbrugsplads" data-testid={`sa-ext-site-name-${i}`} />
            <input className={input} value={s.address} onChange={(e) => setSites(sites.map((x, j) => (j === i ? { ...x, address: e.target.value } : x)))} placeholder="Vej og nr." />
            <input className={input} value={s.city} onChange={(e) => setSites(sites.map((x, j) => (j === i ? { ...x, city: e.target.value } : x)))} placeholder="Postnr. og by" />
          </div>
        ))}
        <button type="button" className="text-xs font-semibold underline" onClick={() => setSites([...sites, { name: "", address: "", city: "" }])}>+ én plads mere</button>
        <p className="text-[11px] text-slate-500">Alle valgte logistikfirmaer sættes på pladserne - ret pr. plads bagefter under "Pladser og vognmænd". Mange pladser: Importér fra Excel med kolonnen Vognmænd.</p>
      </div>
      <div className="flex justify-end gap-2">
        <button className={btnGhost} onClick={onClose}>Annuller</button>
        <button className={btnPrimary} disabled={busy || name.trim().length < 2 || !chosen.length} onClick={save} data-testid="sa-ext-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Opret</button>
      </div>
    </Modal>
  );
}

/** Kundens pladser og hvilke logistikfirmaer der kører på hver - og nye pladser. */
export function ExtSitesDialog({ companyId, request, onClose, onDone }) {
  const haulers = usePremiumHaulers();
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState({});
  const [adding, setAdding] = useState(request ? { name: request.site_name || "", address: request.address || "", city: request.city || "", haulers: request.company_id ? [request.company_id] : [] } : null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    axios.get(`${API}/superadmin/external-customers/${companyId}`).then((r) => {
      setData(r.data);
      setEdit(Object.fromEntries((r.data.sites || []).map((s) => [s.id, { address: s.address, city: s.city, haulers: s.haulers, weekly_hours: s.weekly_hours || null }])));
    }).catch((e) => toast.error(formatApiError(e)));
  }, [companyId]);
  useEffect(() => { load(); }, [load]);
  const saveSite = async (s) => {
    setBusy(true);
    try {
      const { data: r } = await axios.put(`${API}/superadmin/external-customers/${companyId}/sites/${s.id}`, { ...edit[s.id], weekly_hours: edit[s.id].weekly_hours || {} });
      toast.success(`${s.name} er gemt${r.added.length || r.removed.length ? ` (${r.added.length} vognmand tilføjet, ${r.removed.length} fjernet)` : ""}`);
      load(); onDone && onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const addSite = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/superadmin/external-customers/${companyId}/sites`, { ...adding, request_id: request?.id || "" });
      toast.success(`${adding.name} er oprettet`); setAdding(null); load(); onDone && onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const name = (id) => (haulers || []).find((h) => h.id === id)?.name || "";
  return (
    <Modal title={`Pladser og vognmænd${data ? ` - ${data.name}` : ""}`} onClose={onClose} testid="sa-ext-sites">
      {!data ? <Loader2 className="w-5 h-5 animate-spin" /> : (
        <>
          <p className="text-xs text-slate-500">Hver plads findes én gang. Vælg de logistikfirmaer, der kører på den - de ser kun deres egne pladser og har hver deres materialer, containere og navne. Fjernes et firma fra en plads, fjernes dets materialer og containere dér (ture og bestillinger bevares). Navnet rettes ikke (det står på turene) - logistikfirmaerne genkender pladsen på deres egne navne.</p>
          <div className="space-y-2">
            {data.sites.length === 0 && <p className="text-sm text-slate-500">Ingen pladser endnu.</p>}
            {data.sites.map((s) => {
              const e = edit[s.id] || { haulers: [] };
              const changed = JSON.stringify(e) !== JSON.stringify({ address: s.address, city: s.city, haulers: s.haulers, weekly_hours: s.weekly_hours || null });
              return (
                <div key={s.id} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2" data-testid={`sa-ext-site-${s.location_code}`}>
                  <div className="flex flex-wrap items-center gap-2 text-sm"><MapPin className="w-4 h-4 text-emerald-600" /><b>{s.name}</b>
                    <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-[10px] font-mono">{s.location_code}</span>
                    {!s.active && <span className="text-[10px] font-bold text-amber-700">skjult</span>}
                    <span className="text-xs text-slate-500">{s.haulers.map(name).filter(Boolean).join(", ") || "ingen vognmand"}</span></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input className={input} value={e.address || ""} onChange={(ev) => setEdit({ ...edit, [s.id]: { ...e, address: ev.target.value } })} placeholder="Vej og nr." />
                    <input className={input} value={e.city || ""} onChange={(ev) => setEdit({ ...edit, [s.id]: { ...e, city: ev.target.value } })} placeholder="Postnr. og by" />
                  </div>
                  <HaulerPick haulers={haulers} value={e.haulers || []} onChange={(v) => setEdit({ ...edit, [s.id]: { ...e, haulers: v } })} testid={`sa-ext-site-haulers-${s.location_code}`} />
                  {/* 2026-10-28: Pladsens åbningstider er fælles, når flere vognmænd kører - rettes her (som adressen). */}
                  <WeeklyHours value={e.weekly_hours} onChange={(w) => setEdit({ ...edit, [s.id]: { ...e, weekly_hours: w } })} testid={`sa-ext-site-week-${s.location_code}`} />
                  {changed && <div className="flex justify-end"><button className={btnPrimary} disabled={busy || !(e.haulers || []).length} onClick={() => saveSite(s)} data-testid={`sa-ext-site-save-${s.location_code}`}>
                    <Check className="w-4 h-4" /> Gem</button></div>}
                </div>
              );
            })}
          </div>
          {adding ? (
            <div className="rounded-xl border border-emerald-200 dark:border-emerald-900 p-3 space-y-2" data-testid="sa-ext-site-new">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input className={input} value={adding.name} onChange={(e) => setAdding({ ...adding, name: e.target.value })} placeholder="Navn, fx Køge Genbrugsplads" autoFocus data-testid="sa-ext-site-new-name" />
                <input className={input} value={adding.address} onChange={(e) => setAdding({ ...adding, address: e.target.value })} placeholder="Vej og nr." data-testid="sa-ext-site-new-address" />
                <input className={input} value={adding.city} onChange={(e) => setAdding({ ...adding, city: e.target.value })} placeholder="Postnr. og by" data-testid="sa-ext-site-new-city" />
              </div>
              <HaulerPick haulers={haulers} value={adding.haulers} onChange={(v) => setAdding({ ...adding, haulers: v })} testid="sa-ext-site-new-haulers" />
              <div className="flex justify-end gap-2">
                <button className={btnGhost} onClick={() => setAdding(null)}>Annuller</button>
                <button className={btnPrimary} disabled={busy || !adding.name.trim() || !adding.haulers.length} onClick={addSite} data-testid="sa-ext-site-new-save"><Check className="w-4 h-4" /> Opret plads</button>
              </div>
            </div>
          ) : (
            <button className={btnGhost} onClick={() => setAdding({ name: "", address: "", city: "", haulers: data.haulers.map((h) => h.id) })} data-testid="sa-ext-site-add"><Plus className="w-4 h-4" /> Ny plads</button>
          )}
        </>
      )}
    </Modal>
  );
}

const CHIP = { ok: "bg-emerald-100 text-emerald-800", exists: "bg-sky-100 text-sky-800", error: "bg-red-100 text-red-800",
  created: "bg-emerald-100 text-emerald-800", attached: "bg-sky-100 text-sky-800", skipped: "bg-slate-200 text-slate-700" };
const CHIP_TXT = { ok: "Ny", exists: "Findes", error: "Ret", created: "Oprettet", attached: "Vognmand sat på", skipped: "Kørte der allerede" };

/**
 * 2026-10-28: En anmodning med mange pladser (fx når en ny kunde sættes op) godkendes som en liste:
 * kunden, vognmændene og de valgte pladser i ét tryk. Listen er kontrolleret igen, da siden blev
 * åbnet; hver plads kontrolleres en sidste gang, når den oprettes. Rækker kan rettes eller fravælges.
 */
export function ExtBulkDialog({ request, onClose, onDone }) {
  const haulers = usePremiumHaulers();
  const ex = request.existing_customer;
  const [useExisting, setUseExisting] = useState(!!ex?.external);
  const [name, setName] = useState(request.customer_name || "");
  const [chosen, setChosen] = useState(request.company_id ? [request.company_id] : []);
  const [rows, setRows] = useState((request.sites || []).map((r) => ({ ...r, sel: r.status !== "error" && !(r.status === "exists" && r.attached) })));
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const sel = rows.filter((r) => r.sel);
  const setRow = (i, patch) => setRows(rows.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/superadmin/master-requests/${request.id}/apply`, {
        customer_id: request.customer_id || (useExisting && ex?.external ? ex.id : ""), customer_name: name, haulers: chosen,
        rows: sel.map(({ name: n, address, city, note }) => ({ name: n, address, city, note })) });
      setRes(data);
      (data.open ? toast.warning : toast.success)(data.summary || "Gjort");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const isNew = request.kind === "customer" && !request.customer_id;
  return (
    <Modal title={`Anmodning fra ${request.logistics_name || "et logistikfirma"}: ${isNew ? `ny kunde ${request.customer_name}` : `pladser hos ${request.customer_name}`}`}
      onClose={() => (res ? onDone() : onClose())} testid="sa-bulk">
      {request.note && <p className="text-xs rounded-lg bg-amber-50 dark:bg-amber-900/30 p-2"><b>Note:</b> {request.note}</p>}
      {res ? (
        <div className="space-y-2" data-testid="sa-bulk-result">
          <p className="text-sm font-semibold">{res.summary}</p>
          <ul className="text-xs space-y-1">{res.results.map((r, i) => (
            <li key={i} className="flex flex-wrap items-center gap-2"><span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${CHIP[r.result] || ""}`}>{CHIP_TXT[r.result] || r.result}</span>
              <b>{r.name}</b>{r.location_code && <span className="font-mono text-slate-500">{r.location_code}</span>}{r.message && <span className="text-slate-600">{r.message}</span>}</li>))}</ul>
          {res.open && <p className="text-xs text-amber-800">Pladserne, der ikke kunne oprettes, venter i anmodningen - ret dem og godkend igen.</p>}
          <div className="flex justify-end"><button className={btnPrimary} onClick={onDone} data-testid="sa-bulk-close"><Check className="w-4 h-4" /> Luk</button></div>
        </div>
      ) : (
        <>
          {isNew && (
            <div className="space-y-2">
              {ex && (ex.external ? (
                <label className="flex items-start gap-2 text-sm rounded-lg bg-sky-50 dark:bg-sky-900/30 p-2">
                  <input type="checkbox" className="mt-0.5 w-4 h-4" checked={useExisting} onChange={(e) => setUseExisting(e.target.checked)} data-testid="sa-bulk-existing" />
                  <span><b>{ex.name}</b> findes allerede som kunde med pladser - læg pladserne på den (og sæt vognmanden på) i stedet for at oprette kunden igen.</span>
                </label>
              ) : <p className="text-xs rounded-lg bg-amber-50 dark:bg-amber-900/30 p-2">Et firma, der hedder <b>{ex.name}</b>, bruger selv ContainerFlow. Er det den samme kunde, så afvis anmodningen - ellers giv den nye kunde et andet navn.</p>)}
              {!(useExisting && ex?.external) && <div><span className={lbl}>Kundens navn</span><input className={input} value={name} onChange={(e) => setName(e.target.value)} data-testid="sa-bulk-name" /></div>}
            </div>
          )}
          <div><span className={lbl}>Logistikfirmaer, der kører på pladserne</span><HaulerPick haulers={haulers} value={chosen} onChange={setChosen} testid="sa-bulk-haulers" /></div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2"><span className={`${lbl} flex-1 !mb-0`}>Pladser ({sel.length} af {rows.length} valgt)</span>
              <button type="button" className="text-xs underline" onClick={() => setRows(rows.map((r) => ({ ...r, sel: true })))}>Vælg alle</button>
              <button type="button" className="text-xs underline" onClick={() => setRows(rows.map((r) => ({ ...r, sel: false })))}>Fravælg alle</button></div>
            <div className="max-h-[45vh] overflow-y-auto space-y-1.5 pr-1" data-testid="sa-bulk-rows">
              {rows.map((r, i) => (
                <div key={i} className={`rounded-lg border p-2 space-y-1 ${r.sel ? "border-emerald-300 dark:border-emerald-800" : "border-slate-200 dark:border-slate-700 opacity-70"}`} data-testid={`sa-bulk-row-${i}`}>
                  <div className="grid grid-cols-[auto_1fr] sm:grid-cols-[auto_1.2fr_1.2fr_1fr] gap-2 items-center">
                    <input type="checkbox" className="w-4 h-4 accent-emerald-600" checked={r.sel} onChange={(e) => setRow(i, { sel: e.target.checked })} data-testid={`sa-bulk-sel-${i}`} />
                    <input className={input} value={r.name} onChange={(e) => setRow(i, { name: e.target.value })} placeholder="Navn" />
                    <input className={input} value={r.address} onChange={(e) => setRow(i, { address: e.target.value })} placeholder="Vej og nr." data-testid={`sa-bulk-address-${i}`} />
                    <input className={input} value={r.city} onChange={(e) => setRow(i, { city: e.target.value })} placeholder="Postnr. og by" data-testid={`sa-bulk-city-${i}`} />
                  </div>
                  <div className="text-[11px] flex flex-wrap items-center gap-1.5 pl-6"><span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${CHIP[r.status] || ""}`}>{CHIP_TXT[r.status] || r.status}</span>
                    {[...(r.errors || []), ...(r.warnings || [])].map((m) => <span key={m} className={(r.errors || []).includes(m) ? "text-red-700" : "text-slate-500"}>{m}</span>)}
                    {r.note && <span className="text-slate-500">· {r.note}</span>}</div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-500">Fravalgte pladser afvises (logistikfirmaet ser det). En plads, der findes, får vognmanden på. Kan en plads ikke oprettes (fx uden adresse), venter den i anmodningen.</p>
          </div>
          <div className="flex justify-end gap-2">
            <button className={btnGhost} onClick={onClose}>Annuller</button>
            <button className={btnPrimary} disabled={busy || !sel.length || !chosen.length || (isNew && !(useExisting && ex?.external) && name.trim().length < 2)} onClick={apply} data-testid="sa-bulk-apply">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Godkend {sel.length} {sel.length === 1 ? "plads" : "pladser"}</button>
          </div>
        </>
      )}
    </Modal>
  );
}

/** Anmodninger fra logistikfirmaerne (ny kunde med pladser / ny plads). */
export function ExtRequests({ onChanged }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);   // {kind, req}
  const load = useCallback(() => {
    axios.get(`${API}/superadmin/master-requests`).then((r) => setList(r.data || [])).catch(() => setList([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const reject = async (r) => {
    const note = window.prompt("Afvis anmodningen - begrundelse til logistikfirmaet (valgfri):", "");
    if (note === null) return;
    try { await axios.post(`${API}/superadmin/master-requests/${r.id}/close`, { status: "rejected", note }); toast.success("Afvist"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const done = () => { setOpen(null); load(); onChanged && onChanged(); };
  if (!list.length) return null;
  return (
    <div className="mb-4 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/20 p-3 space-y-2" data-testid="sa-requests">
      <div className="font-bold text-sm">Anmodninger fra logistikfirmaer ({list.length})</div>
      {list.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`sa-request-${r.id}`}>
          <span className="flex-1 min-w-[220px]"><b>{r.logistics_name || "Et logistikfirma"}</b>: {(r.sites || []).length
            ? (r.kind === "site" || r.customer_id ? `${r.sites.length} ${r.sites.length === 1 ? "ny plads" : "nye pladser"} hos ${r.customer_name}`
              : `ny kunde med pladser "${r.customer_name}" (${r.sites.length} ${r.sites.length === 1 ? "plads" : "pladser"})`)
            : r.kind === "site" ? `ny plads "${r.site_name}" hos ${r.customer_name}` : `ny kunde med pladser "${r.customer_name}"`}
            {(r.address || r.city) && <span className="text-slate-500"> · {[r.address, r.city].filter(Boolean).join(", ")}</span>}
            {r.note && <span className="text-slate-500"> · {r.note}</span>}</span>
          <button className={btnPrimary} onClick={() => setOpen({ kind: (r.sites || []).length ? "bulk" : r.kind, req: r })} data-testid={`sa-request-open-${r.id}`}>
            <Plus className="w-4 h-4" /> {(r.sites || []).length ? "Gennemgå" : "Opret"}</button>
          <button className={btnGhost} onClick={() => reject(r)}>Afvis</button>
        </div>
      ))}
      {open?.kind === "bulk" && <ExtBulkDialog request={open.req} onClose={() => setOpen(null)} onDone={done} />}
      {open?.kind === "customer" && <ExtCustomerDialog request={open.req} onClose={() => setOpen(null)} onDone={done} />}
      {open?.kind === "site" && <ExtSitesDialog companyId={open.req.customer_id} request={open.req} onClose={() => setOpen(null)} onDone={done} />}
    </div>
  );
}
